import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';

const RISK_COLOR = { CRITICAL: '#dc2626', HIGH: '#ea580c', MEDIUM: '#d97706', LOW: '#16a34a' };
const STEP_STATUS_COLOR = { PROPOSED: '#7c3aed', EXECUTED: '#16a34a', FAILED: '#dc2626', REJECTED: '#6b7280' };

// MAYA — the self-trained offensive model (served as cataam-rt-coder-30b). User-facing copy in
// this file always says "MAYA"; the model/tool identifier itself (what the backend actually
// calls) is unrelated and never shown here.
const MAYA = 'MAYA';

const tok = () => localStorage.getItem('authToken');
const authHeaders = () => ({ Authorization: `Bearer ${tok()}` });

// Org self-service Red Team Planning — the plain-org-admin counterpart to Admin/RedTeam/RedTeamPlanning.js
// (platform staff) and Partner/PartnerRedTeam.js (resellers). Deliberately has NO org/domain picker: every
// call hits the backend's /api/red-team/self/* endpoints, which resolve the caller's own org from their
// session (see RedTeamResource.requireOrgSelfServiceEligible) — there is nothing here for this component
// to even let the user pick wrong.
export default function OrgRedTeamPlanning() {
  const { t } = useTranslation();
  const [summary, setSummary]     = useState(null);
  const [summaryErr, setSummaryErr] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [plans, setPlans]         = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genErr, setGenErr]       = useState(null);
  const [genResult, setGenResult] = useState(null);
  const [expandedPlanId, setExpandedPlanId] = useState(null);
  const [planDetail, setPlanDetail]         = useState(null);
  const [planDetailLoading, setPlanDetailLoading] = useState(false);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryErr(null);
    try {
      const r = await axiosInstance.get('/red-team/self/summary', { headers: authHeaders() });
      setSummary(r.data);
    } catch (e) {
      setSummary(null);
      setSummaryErr(e.response?.data?.error || e.message);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const r = await axiosInstance.get('/red-team/self/plans', { headers: authHeaders() });
      setPlans(r.data || []);
    } catch { /* non-fatal — summary error already covers the no-domain case */ }
    finally { setPlansLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); loadPlans(); }, [loadSummary, loadPlans]);

  const handleGenerate = async () => {
    setGenerating(true); setGenErr(null); setGenResult(null);
    try {
      const r = await axiosInstance.post('/red-team/self/generate', {}, { headers: authHeaders() });
      setGenResult(r.data);
      loadPlans();
    } catch (e) {
      setGenErr(e.response?.data?.error || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const toggleExpand = async (planId) => {
    if (expandedPlanId === planId) { setExpandedPlanId(null); setPlanDetail(null); return; }
    setExpandedPlanId(planId);
    setPlanDetail(null);
    setPlanDetailLoading(true);
    try {
      const r = await axiosInstance.get(`/red-team/self/plans/${planId}`, { headers: authHeaders() });
      setPlanDetail(r.data);
    } catch { setPlanDetail(null); }
    finally { setPlanDetailLoading(false); }
  };

  // ── Phase execution (MAYA proposes one step at a time, human approves, ASM runs it) ───────
  const [expandedExecPhaseId, setExpandedExecPhaseId] = useState(null);
  const [execSteps, setExecSteps]       = useState({});   // phaseId -> [step, ...]
  const [execStepsLoading, setExecStepsLoading] = useState(false);
  const [proposing, setProposing]       = useState(false);
  const [proposeErr, setProposeErr]     = useState(null);
  const [actingStepId, setActingStepId] = useState(null);

  const loadExecSteps = async (phaseId) => {
    setExecStepsLoading(true);
    try {
      const r = await axiosInstance.get(`/red-team/self/phases/${phaseId}/steps`, { headers: authHeaders() });
      setExecSteps(prev => ({ ...prev, [phaseId]: r.data || [] }));
    } catch { setExecSteps(prev => ({ ...prev, [phaseId]: [] })); }
    finally { setExecStepsLoading(false); }
  };

  const toggleExecPanel = (phaseId) => {
    if (expandedExecPhaseId === phaseId) { setExpandedExecPhaseId(null); return; }
    setExpandedExecPhaseId(phaseId);
    setProposeErr(null);
    if (!execSteps[phaseId]) loadExecSteps(phaseId);
  };

  const askMayaForNextStep = async (phaseId) => {
    setProposing(true); setProposeErr(null);
    try {
      const r = await axiosInstance.post(`/red-team/self/phases/${phaseId}/propose-step`, {}, { headers: authHeaders() });
      setExecSteps(prev => ({ ...prev, [phaseId]: [...(prev[phaseId] || []), r.data] }));
    } catch (e) {
      setProposeErr(e.response?.data?.error || e.message);
    } finally {
      setProposing(false);
    }
  };

  const actOnStep = async (stepId, phaseId, approve) => {
    setActingStepId(stepId);
    try {
      const r = await axiosInstance.post(`/red-team/self/steps/${stepId}/${approve ? 'approve' : 'reject'}`, {},
        { headers: authHeaders() });
      setExecSteps(prev => ({
        ...prev,
        [phaseId]: (prev[phaseId] || []).map(s => s.id === stepId ? r.data : s),
      }));
    } catch (e) {
      setProposeErr(e.response?.data?.error || e.message);
    } finally {
      setActingStepId(null);
    }
  };

  const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px' };
  const pill = (color) => ({ fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 999,
    background: color + '14', color, border: `1px solid ${color}30`, display: 'inline-flex', alignItems: 'center', gap: 6 });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '24px 28px', maxWidth: 1100 }}>
      <div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="fas fa-skull-crossbones" style={{ color: '#7c3aed' }} />
          {t('OrgRedTeamPlanning.title', 'Red Team Planning')}
        </h1>
        <p style={{ margin: '6px 0 0', fontSize: 14, color: '#64748b' }}>
          {t('OrgRedTeamPlanning.subtitle',
            'Claude Opus analyzes your own ASM and internal attack-surface (iASM/BAS) findings to build a prioritized attack plan — powered by Claude Opus.')}
        </p>
      </div>

      {/* Intelligence summary — no domain picker, always this org's own data */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#475569' }}>
            {t('OrgRedTeamPlanning.intelligenceFeeding', 'Intelligence feeding this plan')}
          </div>
          {summary?.resolvedDomainNote && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              <i className="fas fa-info-circle" style={{ marginRight: 4 }} />{summary.resolvedDomainNote}
            </span>
          )}
        </div>

        {summaryLoading && (
          <div style={{ color: '#9ca3af', fontSize: 14 }}><i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
            {t('OrgRedTeamPlanning.loadingInputs', 'Gathering ASM and iASM findings…')}</div>
        )}

        {!summaryLoading && summaryErr && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#b91c1c', fontSize: 14 }}>
            <i className="fas fa-exclamation-triangle" />{summaryErr}
          </div>
        )}

        {!summaryLoading && summary && (
          <>
            {/* The two source counts — the whole point of this view over the old admin-only one */}
            <div style={{ display: 'flex', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#1d4ed8', lineHeight: 1 }}>{summary.asmInputCount}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginTop: 4 }}>
                  <i className="fas fa-globe" style={{ marginRight: 5 }} />
                  {t('OrgRedTeamPlanning.fromAsm', 'inputs from ASM')}
                </div>
                <div style={{ fontSize: 11, color: '#60a5fa', marginTop: 2 }}>
                  {t('OrgRedTeamPlanning.asmDesc', 'External recon — subdomains, CVEs, exposed paths')}
                </div>
              </div>
              <div style={{ flex: '1 1 200px', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#6d28d9', lineHeight: 1 }}>{summary.iasmInputCount}</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#5b21b6', marginTop: 4 }}>
                  <i className="fas fa-network-wired" style={{ marginRight: 5 }} />
                  {t('OrgRedTeamPlanning.fromIasm', 'inputs from iASM')}
                </div>
                <div style={{ fontSize: 11, color: '#a78bfa', marginTop: 2 }}>
                  {summary.iasmBasVerifiedCount > 0
                    ? t('OrgRedTeamPlanning.iasmDescWithBas', '{{count}} execution-verified (BAS)', { count: summary.iasmBasVerifiedCount })
                    : t('OrgRedTeamPlanning.iasmDesc', 'Authenticated cloud/connector discovery')}
                </div>
              </div>
            </div>

            {/* Severity breakdown across both sources */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: summary.byModule ? 12 : 0 }}>
              {summary.criticalCount > 0 && <span style={pill(RISK_COLOR.CRITICAL)}>{summary.criticalCount} {t('OrgRedTeamPlanning.critical', 'critical')}</span>}
              {summary.highCount > 0 && <span style={pill(RISK_COLOR.HIGH)}>{summary.highCount} {t('OrgRedTeamPlanning.high', 'high')}</span>}
              {summary.mediumCount > 0 && <span style={pill(RISK_COLOR.MEDIUM)}>{summary.mediumCount} {t('OrgRedTeamPlanning.medium', 'medium')}</span>}
              {summary.subdomainCount > 0 && <span style={pill('#7c3aed')}>{summary.subdomainCount} {t('OrgRedTeamPlanning.subdomains', 'subdomains')}</span>}
            </div>

            {summary.byModule && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(summary.byModule).filter(([, v]) => v > 0).map(([key, val]) => (
                  <span key={key} style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 5,
                    background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }}>
                    {key}: {val}
                  </span>
                ))}
              </div>
            )}

            <button onClick={handleGenerate} disabled={generating || summary.totalInputCount === 0}
              style={{ marginTop: 18, height: 42, padding: '0 22px',
                background: generating || summary.totalInputCount === 0 ? '#e2e8f0' : 'linear-gradient(135deg,#7c3aed,#4f46e5)',
                color: generating || summary.totalInputCount === 0 ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8,
                fontSize: 14, fontWeight: 700, cursor: generating || summary.totalInputCount === 0 ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className={`fas ${generating ? 'fa-spinner fa-spin' : 'fa-robot'}`} />
              {generating
                ? t('OrgRedTeamPlanning.generating', 'Opus is planning…')
                : summary.totalInputCount === 0
                  ? t('OrgRedTeamPlanning.noFindings', 'No findings yet to plan from')
                  : t('OrgRedTeamPlanning.generateBtn', 'Generate Attack Plan')}
            </button>
          </>
        )}

        {genErr && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14,
            background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '12px 16px', color: '#b91c1c', fontSize: 13 }}>
            <i className="fas fa-exclamation-triangle" />{genErr}
          </div>
        )}

        {genResult && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 14,
            background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '12px 16px', color: '#166534', fontSize: 13 }}>
            <i className="fas fa-check-circle" style={{ fontSize: 16, color: '#22c55e' }} />
            <div>
              <strong>{t('OrgRedTeamPlanning.planGenerated', 'Plan generated')} </strong>
              <span style={{ fontWeight: 700, color: RISK_COLOR[genResult.riskRating] }}>{genResult.riskRating}</span>
              <span> · {genResult.phaseCount} {t('OrgRedTeamPlanning.phases', 'phases')} · {genResult.estimatedTta}</span>
            </div>
          </div>
        )}
      </div>

      {/* Plan history */}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
            <i className="fas fa-history" style={{ marginRight: 8, color: '#7c3aed' }} />
            {t('OrgRedTeamPlanning.history', 'Attack Plan History')}
          </h3>
          <button onClick={loadPlans} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', fontSize: 12, color: '#6b7280', cursor: 'pointer' }}>
            <i className={`fas fa-sync ${plansLoading ? 'fa-spin' : ''}`} />
          </button>
        </div>

        {plansLoading && <div style={{ padding: 20, color: '#9ca3af', fontSize: 14 }}><i className="fas fa-spinner fa-spin" /> {t('OrgRedTeamPlanning.loading', 'Loading…')}</div>}
        {!plansLoading && plans.length === 0 && <div style={{ padding: 20, color: '#9ca3af', fontSize: 14 }}>{t('OrgRedTeamPlanning.noPlansYet', 'No attack plans generated yet.')}</div>}

        {plans.map((p) => (
          <div key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <div onClick={() => toggleExpand(p.id)} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', cursor: 'pointer' }}>
              <i className={`fas fa-chevron-${expandedPlanId === p.id ? 'down' : 'right'}`} style={{ color: '#9ca3af', fontSize: 11 }} />
              <span style={pill(RISK_COLOR[p.riskRating] || '#6b7280')}>{p.riskRating || '—'}</span>
              <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{p.phaseCount} {t('OrgRedTeamPlanning.phases', 'phases')} · {p.estimatedTta}</span>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>{p.createdAt ? new Date(p.createdAt).toLocaleString() : ''}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: p.status === 'COMPLETED' ? '#16a34a' : p.status === 'FAILED' ? '#dc2626' : '#d97706' }}>
                {p.status}
              </span>
            </div>
            {expandedPlanId === p.id && (
              <div style={{ padding: '0 20px 18px 48px' }}>
                {planDetailLoading && <div style={{ color: '#9ca3af', fontSize: 13 }}><i className="fas fa-spinner fa-spin" /> {t('OrgRedTeamPlanning.loading', 'Loading…')}</div>}
                {planDetail && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <p style={{ fontSize: 13, color: '#374151', margin: 0, whiteSpace: 'pre-wrap' }}>{planDetail.executiveSummary}</p>
                    {(planDetail.phases || []).map((phase, i) => (
                      <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b' }}>
                          {t('OrgRedTeamPlanning.phase', 'Phase')} {phase.phaseNumber}: {phase.phaseName}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{phase.objective}</div>

                        <button onClick={() => toggleExecPanel(phase.id)}
                          style={{ marginTop: 10, background: 'none', border: '1px solid #ddd6fe', color: '#6d28d9',
                            borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <i className={`fas fa-chevron-${expandedExecPhaseId === phase.id ? 'down' : 'right'}`} style={{ fontSize: 9 }} />
                          <i className="fas fa-robot" />
                          {t('OrgRedTeamPlanning.executeWithMaya', 'Execute with {{maya}}', { maya: MAYA })}
                        </button>

                        {expandedExecPhaseId === phase.id && (
                          <div style={{ marginTop: 10, background: '#fff', border: '1px solid #ddd6fe', borderRadius: 8, padding: 14 }}>
                            <p style={{ fontSize: 11, color: '#7c3aed', margin: '0 0 10px', fontWeight: 600 }}>
                              <i className="fas fa-info-circle" style={{ marginRight: 5 }} />
                              {t('OrgRedTeamPlanning.execExplainer',
                                '{{maya}} proposes ONE next tool call at a time, drawn from ASM\'s existing safety-gated tool catalog. Nothing runs until you approve it.', { maya: MAYA })}
                            </p>

                            {execStepsLoading && <div style={{ color: '#9ca3af', fontSize: 13 }}><i className="fas fa-spinner fa-spin" /> {t('OrgRedTeamPlanning.loading', 'Loading…')}</div>}

                            {!execStepsLoading && (execSteps[phase.id] || []).map((step) => (
                              <div key={step.id} style={{ borderTop: '1px solid #f1f5f9', padding: '10px 0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af' }}>#{step.stepNumber}</span>
                                  {step.modelDoneSignal ? (
                                    <span style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                                      <i className="fas fa-flag-checkered" style={{ marginRight: 5 }} />
                                      {t('OrgRedTeamPlanning.mayaSignalsDone', '{{maya}} signals this phase is complete', { maya: MAYA })}
                                    </span>
                                  ) : (
                                    <>
                                      <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>{step.proposedTool}</span>
                                      <span style={{ fontSize: 12, color: '#64748b', fontFamily: 'monospace' }}>{step.proposedTarget}</span>
                                      <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '1px 8px', borderRadius: 999,
                                        color: STEP_STATUS_COLOR[step.status] || '#6b7280', background: (STEP_STATUS_COLOR[step.status] || '#6b7280') + '14' }}>
                                        {step.status}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {step.proposedReasoning && (
                                  <div style={{ fontSize: 12, color: '#6d28d9', marginTop: 4, fontStyle: 'italic' }}>
                                    "{step.proposedReasoning}"
                                  </div>
                                )}
                                {!step.modelDoneSignal && step.proposedArgs && (
                                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                    {step.proposedArgs}
                                  </div>
                                )}
                                {step.resultSummary && (
                                  <div style={{ fontSize: 11, color: '#475569', marginTop: 6, background: '#f8fafc',
                                    border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 8px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    {step.resultSummary}
                                  </div>
                                )}
                                {step.status === 'PROPOSED' && !step.modelDoneSignal && (
                                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <button onClick={() => actOnStep(step.id, phase.id, true)} disabled={actingStepId === step.id}
                                      style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 6,
                                        padding: '4px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                                      {actingStepId === step.id ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}{' '}
                                      {t('OrgRedTeamPlanning.approveExecute', 'Approve & Run via ASM')}
                                    </button>
                                    <button onClick={() => actOnStep(step.id, phase.id, false)} disabled={actingStepId === step.id}
                                      style={{ background: 'none', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 6,
                                        padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                                      {t('OrgRedTeamPlanning.reject', 'Reject')}
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}

                            {proposeErr && (
                              <div style={{ marginTop: 8, fontSize: 12, color: '#b91c1c', background: '#fef2f2',
                                border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>
                                {proposeErr}
                              </div>
                            )}

                            <button onClick={() => askMayaForNextStep(phase.id)} disabled={proposing}
                              style={{ marginTop: 10, background: proposing ? '#e2e8f0' : 'linear-gradient(135deg,#7c3aed,#4f46e5)',
                                color: proposing ? '#9ca3af' : '#fff', border: 'none', borderRadius: 8,
                                padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: proposing ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', gap: 6 }}>
                              <i className={`fas ${proposing ? 'fa-spinner fa-spin' : 'fa-robot'}`} />
                              {proposing
                                ? t('OrgRedTeamPlanning.mayaThinking', '{{maya}} is deciding the next step…', { maya: MAYA })
                                : t('OrgRedTeamPlanning.askMayaNext', 'Ask {{maya}} for next step', { maya: MAYA })}
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
